import { useRef, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useZoom } from 'context/ZoomContext';
import { ClearAttributeMappingsPayload } from 'services/sheet-attribute-manager/models/sheet-attribute-manager.interface';
import { MappingActionsToolbarProps } from './MappingActionsToolbar.props';
import {
  getClearMappingsButtonSx,
  getCreateLabelsButtonSx,
  getCreateLabelsTextSx,
  getMenuItemSx,
  getMenuListSx,
  getMenuPaperSx,
  getPushMappingsButtonSx,
  getToolbarContainerSx,
  popperSx,
} from './MappingActionsToolbar.styles';
import { XLSX_VIEWER_STRINGS } from './xlsx-viewer.contants';

const MappingActionsToolbar = ({
  documentReferenceId,
  activeSheetLabel,
  leadingActions,
  onPushMappings,
  onClearMappings,
  onReprocessDocument,
  primaryReprocessLabel,
  showCreateLabels = true,
  showPushMappings = false,
}: MappingActionsToolbarProps) => {
  const { zoomLevel } = useZoom();
  const [open, setOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingClearPayload, setPendingClearPayload] = useState<ClearAttributeMappingsPayload>();
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleOpenConfirmDialog = (payload?: ClearAttributeMappingsPayload) => {
    setPendingClearPayload(payload);
    setConfirmDialogOpen(true);
    setOpen(false);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setPendingClearPayload(undefined);
  };

  const handleConfirmClearMappings = () => {
    onClearMappings(pendingClearPayload);
    handleCloseConfirmDialog();
  };

  const handleCreateLabels = () => {
    onReprocessDocument?.();
    setOpen(false);
  };

  const hasActiveSheetLabel = Boolean(activeSheetLabel);

  return (
    <>
      <Box sx={getToolbarContainerSx(zoomLevel)}>
        {leadingActions}

        {showCreateLabels && (
          <Button variant="text" size="small" onClick={handleCreateLabels} sx={getCreateLabelsButtonSx(zoomLevel)}>
            <Typography component="span" sx={getCreateLabelsTextSx(zoomLevel)}>
              {primaryReprocessLabel ?? XLSX_VIEWER_STRINGS.BUTTON.CREATE_LABELS}
            </Typography>
          </Button>
        )}

        {showPushMappings && (
          <Tooltip title="Push Mappings">
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={onPushMappings}
              sx={getPushMappingsButtonSx(zoomLevel)}>
              {XLSX_VIEWER_STRINGS.BUTTON.PUSH_MAPPINGS}
            </Button>
          </Tooltip>
        )}

        <Tooltip title={XLSX_VIEWER_STRINGS.MENU.CLEAR_MENU_TOOLTIP}>
          <IconButton
            ref={anchorRef}
            size="small"
            onClick={handleToggle}
            aria-controls={open ? XLSX_VIEWER_STRINGS.ARIA.MENU_ID : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="menu"
            sx={getClearMappingsButtonSx(zoomLevel)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Popper
        sx={popperSx}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-end">
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}>
            <Paper sx={getMenuPaperSx(zoomLevel)}>
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  <MenuList autoFocusItem id={XLSX_VIEWER_STRINGS.ARIA.MENU_ID} sx={getMenuListSx(zoomLevel)}>
                    <MenuItem
                      disabled={!hasActiveSheetLabel}
                      sx={getMenuItemSx(zoomLevel)}
                      title={activeSheetLabel}
                      onClick={() =>
                        handleOpenConfirmDialog({
                          documentReferenceId,
                          sheet: activeSheetLabel,
                        })
                      }>
                      {XLSX_VIEWER_STRINGS.MENU.CLEAR_SHEET}
                    </MenuItem>
                    <MenuItem
                      sx={getMenuItemSx(zoomLevel)}
                      onClick={() => handleOpenConfirmDialog({ documentReferenceId })}>
                      {XLSX_VIEWER_STRINGS.MENU.CLEAR_DOCUMENT}
                    </MenuItem>
                    <MenuItem sx={getMenuItemSx(zoomLevel)} onClick={() => handleOpenConfirmDialog()}>
                      {XLSX_VIEWER_STRINGS.MENU.CLEAR_ALL}
                    </MenuItem>
                  </MenuList>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog}>
        <DialogTitle>{XLSX_VIEWER_STRINGS.DIALOG.TITLE}</DialogTitle>
        <DialogContent>
          <DialogContentText>{XLSX_VIEWER_STRINGS.DIALOG.CONTENT}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            {XLSX_VIEWER_STRINGS.DIALOG.CANCEL}
          </Button>
          <Button onClick={handleConfirmClearMappings} color="error" variant="contained">
            {XLSX_VIEWER_STRINGS.DIALOG.CONFIRM}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MappingActionsToolbar;
